import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Bot, Code2, CreditCard, Globe, Headphones, ShoppingCart, Sparkles, AlertTriangle, Zap, CheckCircle2, Calendar, RefreshCw, Cog, Bolt, Shield } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Section, SectionHeader, TerminalWindow, CTABlock, TestimonialsSection } from "@/components/shared/Shared";
import CalendlyModal from "@/components/shared/CalendlyModal";
import SEO from "@/components/shared/SEO";
import TypewriterHeadline from "@/components/shared/TypewriterHeadline";

const fadeUp = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const homepageJsonLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "OnePix",
  "description": "Boutique web development agency specializing in custom web systems, AI integrations, eCommerce, and Laravel development.",
  "url": "https://onepix.net",
  "email": "info@onepix.net",
  "foundingDate": "2018",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Miami",
    "addressRegion": "FL",
    "addressCountry": "US",
  },
  "sameAs": [
    "https://www.linkedin.com/company/onepixteam",
    "https://t.me/onepixteam",
  ],
};

// ─── Hero ───────────────────────────────────────────────────────────────────
function Hero() {
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  return (
    <section className="relative overflow-hidden py-24 lg:py-36">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-primary/10 blur-[120px] will-change-transform" style={{ contain: 'layout style' }} />
      <div className="absolute right-1/4 bottom-1/4 h-96 w-96 rounded-full bg-secondary/10 blur-[120px] will-change-transform" style={{ contain: 'layout style' }} />

      <div className="container relative mx-auto grid gap-12 px-4 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="flex flex-col justify-center"
        >
          {/* Badge */}
          <span className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-medium text-primary">
            🤖 AI-Powered Development Agency · Miami, FL
          </span>

          {/* Prefix */}
          <p className="text-lg font-medium text-muted-foreground sm:text-xl">
            We build software that
          </p>

          {/* Typewriter H1 */}
          <h1 className="mt-2 text-3xl font-extrabold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl min-h-[2.5em] sm:min-h-[2em]">
            <TypewriterHeadline />
          </h1>

          {/* Suffix */}
          <p className="mt-2 text-lg font-medium text-muted-foreground sm:text-xl">
            — and you own it forever.
          </p>

          {/* Body */}
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
            OnePix is a boutique development team. We build custom AI tools, web systems, and eCommerce platforms. 300+ projects. 36 countries. 7 years. One rule: you own the product, not the subscription.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-4">
            <button
              onClick={() => setCalendlyOpen(true)}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-6 text-sm font-medium text-primary shadow-lg shadow-primary/10 transition-all hover:bg-primary/10 hover:shadow-primary/20"
            >
              <Calendar className="h-4 w-4" /> Book a Free Call →
            </button>
            <a href="https://wa.me/message/7LSTVCZDQZ27H1" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="glow">WhatsApp</Button>
            </a>
            <a href="#case-studies-section">
              <Button size="lg" variant="ghost-bright">See Our Work ↓</Button>
            </a>
          </div>

          {/* Stats row */}
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
            {["300+ Projects", "36 Countries", "7 Years", "86% Long-term Clients"].map((s) => (
              <span key={s} className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {s}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="hidden lg:flex items-center justify-center"
        >
          <TerminalWindow title="roi-calculator.ts">
            <div><span className="text-muted-foreground">// Your current stack:</span></div>
            <div><span className="text-foreground">monthly_costs</span> <span className="text-muted-foreground">=</span> <span className="text-primary">crm</span><span className="text-muted-foreground">(</span><span className="text-foreground">$299</span><span className="text-muted-foreground">)</span> <span className="text-muted-foreground">+</span> <span className="text-primary">analytics</span><span className="text-muted-foreground">(</span><span className="text-foreground">$149</span><span className="text-muted-foreground">)</span> <span className="text-muted-foreground">+</span> <span className="text-primary">support_tool</span><span className="text-muted-foreground">(</span><span className="text-foreground">$399</span><span className="text-muted-foreground">)</span> <span className="text-muted-foreground">+ ...</span></div>
            <div><span className="text-muted-foreground">// = $1,200/month = $14,400/year. Forever.</span></div>
            <div className="mt-3"><span className="text-muted-foreground">// After OnePix:</span></div>
            <div><span className="text-foreground">custom_system</span> <span className="text-muted-foreground">=</span> <span className="text-primary">build_once</span><span className="text-muted-foreground">(</span><span className="text-foreground">exactly_what_you_need</span><span className="text-muted-foreground">)</span></div>
            <div><span className="text-foreground">monthly_costs</span> <span className="text-muted-foreground">=</span> <span className="text-foreground">server_hosting</span>  <span className="text-muted-foreground">// ~$40/month</span></div>
            <div><span className="text-foreground">ownership</span> <span className="text-muted-foreground">=</span> <span className="text-[hsl(var(--terminal-green))]">true</span>  <span className="text-muted-foreground">// yours. always.</span></div>
          </TerminalWindow>
        </motion.div>
      </div>
      <CalendlyModal open={calendlyOpen} onClose={() => setCalendlyOpen(false)} />
    </section>
  );
}

// ─── Trusted By ─────────────────────────────────────────────────────────────
const clients = [
  "Stripe", "Billwerk+", "Joompay", "UCtel", "Conexwest",
  "Nature's Sunshine", "Collar", "Mayora", "Tipple", "Integra Sources", "US Junk Cars",
];

function TrustedBy() {
  return (
    <Section className="py-12 lg:py-16">
      <p className="mb-8 text-center text-sm text-muted-foreground">Trusted by teams across 36 countries</p>
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-background to-transparent" />
        <div className="marquee flex gap-12 whitespace-nowrap">
          {[...clients, ...clients].map((c, i) => (
            <span key={i} className="inline-flex items-center text-sm font-medium text-muted-foreground">{c}</span>
          ))}
        </div>
      </div>
    </Section>
  );
}

// ─── Services Grid ──────────────────────────────────────────────────────────
const servicesData = [
  {
    icon: "🧠",
    title: "AI Development",
    hook: "Build AI products that actually ship",
    desc: "We build AI agents, recommendation engines, document processing pipelines, and intelligent automation systems. Production-grade, not prototypes.",
    href: "/services/ai-development",
  },
  {
    icon: "🔄",
    title: "Custom Tools (Kill Your Subscriptions)",
    hook: "Stop paying monthly. Pay once.",
    desc: "CRM, analytics dashboard, support tool, financial reporting — we build your custom version in weeks. You own it. No vendor lock-in. No recurring fees.",
    href: "/services/custom-development",
  },
  {
    icon: "📱",
    title: "Mobile App Development",
    hook: "iOS & Android apps built to ship",
    desc: "Native and cross-platform mobile apps — React Native and Flutter. From MVP to full-scale product. Connected to your backend, your CRM, your existing web platform.",
    href: "/services/mobile-development",
  },
  {
    icon: "🛒",
    title: "eCommerce Development",
    hook: "WooCommerce & Shopify, done properly",
    desc: "From simple stores to complex B2B platforms with AI-powered recommendations, dynamic pricing, and automated order management.",
    href: "/services/ecommerce-development",
  },
  {
    icon: "⚙️",
    title: "Web Applications (Laravel)",
    hook: "Complex systems, clean architecture",
    desc: "SaaS platforms, internal tools, marketplaces, configurators — built on Laravel with React frontends. Scalable. Documented. Maintainable.",
    href: "/services/custom-development",
  },
  {
    icon: "⚡",
    title: "Process Automation",
    hook: "Give your team their time back",
    desc: "Order processing, document handling, lead qualification, notifications — we automate the repetitive operations that drain your team's day.",
    href: "/services/ai-agents",
  },
  {
    icon: "🛡️",
    title: "Post-Launch Support",
    hook: "We stay after launch. Always.",
    desc: "Monthly retainers for ongoing development, bug fixes, performance monitoring, and feature additions. One team, continuous improvement.",
    href: "/services/post-launch-support",
  },
];

function ServicesGrid() {
  return (
    <Section>
      <SectionHeader
        title="AI-powered development for teams who build to own"
        description="We build production AI systems, replace expensive SaaS subscriptions with custom tools, and deliver eCommerce platforms that actually scale. No templates. No shortcuts."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {servicesData.map((s, i) => (
          <motion.div key={s.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
            <Link
              to={s.href}
              className="glass-card group flex h-full flex-col p-6 transition-all duration-300 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
            >
              <span className="text-3xl">{s.icon}</span>
              <h3 className="mt-4 text-lg font-semibold text-foreground">{s.title}</h3>
              <p className="mt-1 text-sm font-medium text-primary">{s.hook}</p>
              <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              <div className="mt-4 flex items-center text-sm text-primary opacity-0 transition-opacity group-hover:opacity-100">
                Learn more <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

// ─── Pain / Offer / Value ───────────────────────────────────────────────────
const painPoints = [
  "Paying $500/month for a SaaS dashboard that only does 60% of what you need?",
  "Your financial reporting still lives in Google Sheets — and your team hates it.",
  "You've outgrown your off-the-shelf tools but can't justify a full enterprise platform.",
  "Every new feature request means another subscription, another vendor, another integration headache.",
  "Your competitors are shipping AI-powered features. You're still copy-pasting between systems.",
  "You're paying for 5 services to do what one custom tool could handle.",
];

const offerPoints = [
  "We build your own version — one investment, yours forever. No monthly fees, no vendor lock-in.",
  "Your financial dashboard in 1 week — DDS, P&L, project economics, all in one place. Built for your business model.",
  "Paying $1,200/year for a tool? We'll build your custom alternative for $3–5K. Once. Yours forever.",
  "Stop feeding subscriptions — invest once in a system that fits exactly how your team works.",
  "Your competitors are still in spreadsheets. We can change that in 2 weeks.",
  "We build AI into your workflow — not as a gimmick, but as a feature that measurably reduces your operating costs.",
];

const valuePoints = [
  "Cash flow, P&L, project economics — all in one dashboard. No subscription.",
  "A custom AI assistant that handles 40% of your support tickets automatically.",
  "Automated order processing that runs without your team touching it.",
  "A recommendation engine that increases average order value by 20–35%.",
  "A business system that's documented, maintainable, and 100% yours.",
  "One development partner who understands your product — not a ticket queue.",
];

function PainOfferValue() {
  const [calendlyOpen, setCalendlyOpen] = useState(false);

  return (
    <section className="relative py-20 lg:py-28" style={{ backgroundColor: "hsl(240, 20%, 5%)" }}>
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div {...fadeUp} className="mb-14 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-foreground lg:text-4xl">
            Still paying monthly for tools you don't fully own?
          </h2>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* PAIN */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="glass-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <h3 className="text-lg font-bold text-foreground">Sound familiar?</h3>
            </div>
            <ul className="space-y-3">
              {painPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                  {p}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* OFFER */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="glass-card-glow p-6 ring-1 ring-primary/20">
            <div className="mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-bold gradient-text">Here's what we actually do</h3>
            </div>
            <ul className="space-y-3">
              {offerPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {p}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* VALUE */}
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="glass-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--terminal-green))]" />
              <h3 className="text-lg font-bold text-foreground">What you walk away with</h3>
            </div>
            <ul className="space-y-3">
              {valuePoints.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--terminal-green))]" />
                  {p}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div {...fadeUp} className="mt-14 text-center">
          <h3 className="text-xl font-bold text-foreground">Ready to stop renting and start owning?</h3>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => setCalendlyOpen(true)}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-6 text-sm font-medium text-primary shadow-lg shadow-primary/10 transition-all hover:bg-primary/10 hover:shadow-primary/20"
            >
              <Calendar className="h-4 w-4" /> Book a Free Call →
            </button>
            <a href="https://wa.me/message/7LSTVCZDQZ27H1" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="glow">WhatsApp</Button>
            </a>
            <a href="https://t.me/onepixteam" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="ghost-bright">Telegram</Button>
            </a>
          </div>
        </motion.div>
      </div>
      <CalendlyModal open={calendlyOpen} onClose={() => setCalendlyOpen(false)} />
    </section>
  );
}

// ─── Stats Bar ──────────────────────────────────────────────────────────────
const stats = [
  { value: "300+", label: "Projects Launched" },
  { value: "7", label: "Years in Business" },
  { value: "36", label: "Countries Served" },
  { value: "90+", label: "Industry Niches" },
  { value: "86%", label: "Long-term Clients" },
];

function StatsBar() {
  return (
    <div className="border-y border-border/50 bg-surface/50">
      <div className="container mx-auto grid grid-cols-2 gap-8 px-4 py-12 sm:grid-cols-3 lg:grid-cols-5 lg:px-8">
        {stats.map((s, i) => (
          <motion.div key={s.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }} className="text-center">
            <div className="text-3xl font-bold gradient-text">{s.value}</div>
            <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─── AI Spotlight ───────────────────────────────────────────────────────────
const aiCols = [
  { title: "AI Integrations", desc: "Connect GPT-4, Claude, Gemini to your product. RAG pipelines, vector databases, semantic search." },
  { title: "AI Agents & Automation", desc: "Autonomous agents for data processing, content generation, and customer support." },
  { title: "AI-Powered eCommerce", desc: "Intelligent recommendations, dynamic pricing, AI search, personalized catalogs." },
];

function AISpotlight() {
  return (
    <section className="relative overflow-hidden py-20 lg:py-28">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-secondary/5 to-transparent" />
      <div className="container relative mx-auto px-4 lg:px-8">
        <motion.div {...fadeUp} className="mb-14 max-w-2xl">
          <span className="mb-4 inline-block rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-medium text-primary">
            <Sparkles className="mr-1 inline h-3 w-3" /> AI Services
          </span>
          <h2 className="text-3xl font-bold tracking-tight text-foreground lg:text-4xl">
            AI integration for production systems
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            We don't build AI demos. We integrate LLMs, build retrieval pipelines, and deploy autonomous agents into real product workflows — connected to your database, your APIs, your business logic.
          </p>
        </motion.div>
        <div className="grid gap-6 sm:grid-cols-3">
          {aiCols.map((col, i) => (
            <motion.div key={col.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} className="glass-card-glow p-6">
              <h3 className="text-lg font-semibold text-foreground">{col.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{col.desc}</p>
            </motion.div>
          ))}
        </div>
        <motion.div {...fadeUp} className="mt-10">
          <Link to="/services/ai-development">
            <Button variant="ghost-bright" size="lg">
              Explore AI Services <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

// ─── Case Studies Preview ───────────────────────────────────────────────────
import { caseStudies as allCaseStudies } from "@/lib/caseStudies";
const homeCases = allCaseStudies.slice(0, 3);

function CaseStudiesPreview() {
  return (
    <Section id="case-studies-section">
      <SectionHeader title="Selected work" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {homeCases.map((cs, i) => (
          <motion.div key={cs.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
            <Link to={`/case-studies/${cs.slug}`} className="glass-card group block overflow-hidden transition-all hover:border-primary/30 p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">{cs.badge}</span>
              </div>
              <h3 className="text-base font-semibold text-foreground leading-snug">{cs.cardHeadline}</h3>
              <p className="mt-2 text-sm font-bold gradient-text">{cs.resultMetric}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {cs.stack.slice(0, 3).map((t) => (
                  <span key={t} className="rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs font-mono text-muted-foreground">{t}</span>
                ))}
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
      <motion.div {...fadeUp} className="mt-10">
        <Link to="/case-studies" className="inline-flex items-center text-sm text-primary hover:underline">
          All Case Studies <ArrowRight className="ml-1 h-3.5 w-3.5" />
        </Link>
      </motion.div>
    </Section>
  );
}

// ─── Why OnePix ─────────────────────────────────────────────────────────────
const whyBlocks = [
  { title: "High-impact solutions", desc: "We solve real business problems, not just ship code. Every decision is optimized for your business outcomes." },
  { title: "Time & cost efficiency", desc: "Deep experience means fewer iterations and revisions. We've seen most patterns before." },
  { title: "Transparent estimates", desc: "Clear breakdown, no hidden costs or scope creep. You know what you're paying for." },
];

function WhyOnePix() {
  return (
    <Section>
      <SectionHeader title="We work like your in-house team — without the overhead" description="Most of our clients stay with us for 2+ years. Not because of contracts — because we actually understand their product." />
      <div className="grid gap-6 sm:grid-cols-3">
        {whyBlocks.map((b, i) => (
          <motion.div key={b.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
            <h3 className="text-lg font-semibold text-foreground">{b.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
          </motion.div>
        ))}
      </div>
      <motion.div {...fadeUp} className="mt-12 glass-card p-6 flex items-start gap-4 max-w-2xl">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-lg">A</div>
        <div>
          <p className="text-sm text-muted-foreground italic leading-relaxed">
            "Tell us about your project — we'll find the right solution even if your niche is new to us."
          </p>
          <p className="mt-2 text-sm font-medium text-foreground">Artem, Founder</p>
        </div>
      </motion.div>
    </Section>
  );
}

// ─── Blog Preview ───────────────────────────────────────────────────────────
const blogPosts = [
  { title: "How We Build AI Agents for eCommerce", date: "Mar 2026", tag: "AI" },
  { title: "WooCommerce Performance: A Technical Deep Dive", date: "Feb 2026", tag: "WordPress" },
  { title: "Payment Gateway Integration Patterns We've Learned", date: "Jan 2026", tag: "Payments" },
];

function BlogPreview() {
  return (
    <Section>
      <SectionHeader title="From the blog" />
      <div className="grid gap-4 sm:grid-cols-3">
        {blogPosts.map((p, i) => (
          <motion.div key={p.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} className="glass-card group cursor-pointer p-5 transition-all hover:border-primary/30">
            <span className="text-xs font-mono text-primary">{p.tag}</span>
            <h3 className="mt-2 text-base font-semibold text-foreground group-hover:text-primary transition-colors">{p.title}</h3>
            <p className="mt-2 text-xs text-muted-foreground">{p.date}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function Index() {
  return (
    <Layout>
      <SEO
        title="Custom Web Development, AI & eCommerce"
        description="OnePix is a boutique development agency. We build custom AI tools, web systems, and eCommerce platforms. 300+ projects across 36 countries."
        path="/"
        jsonLd={homepageJsonLd}
      />
      <Hero />
      <TrustedBy />
      <ServicesGrid />
      <PainOfferValue />
      <StatsBar />
      <AISpotlight />
      <CaseStudiesPreview />
      <WhyOnePix />
      <TestimonialsSection />
      <BlogPreview />
      <CTABlock />
    </Layout>
  );
}
